import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { DefaultCell, TitleCaseCell, UpCaseCell } from "../../CustomizedTable/CustomCells/CustomCells";
import LoadIdCell from "../../CustomizedTable/CustomCells/OrderIdCell/LoadIdCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import WeightCell from "../../CustomizedTable/CustomCells/WeightCell/WeightCell";
import MilesCell from "../../CustomizedTable/CustomCells/MilesCell/MilesCell";
import NewRowCell from "../../CustomizedTable/CustomCells/NewRowCell/NewRowCell";
import ActionsCell from "../../CustomizedTable/CustomCells/ActionsCell/ActionsCell";
import DeclinedMatchCell from "../../CustomizedTable/CustomCells/DeclinedMatchCell/DeclinedMatchCell";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import SelectColumnFilter from "../../CustomizedTable/Filters/SelectColumnFilter/SelectColumnFilter";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import InlineIconButton from "../../Buttons/InlineIconButton/InlineIconButton";
import StyledTooltip from "../../StyledTooltip/StyledTooltip";
import CachedIcon from "@mui/icons-material/Cached";
import { updateFreightMatches } from "../../../utils/freightMatchingHelpers";

export default function CarrierFreightMatches({
  isLoading,
  activeCarrier,
  freightMatches,
  setFreightMatches,
  newLoadsCount,
  radiusOptions,
  radiusOrigin,
  setRadiusOrigin,
  radiusDest,
  setRadiusDest,
  handleGetMatches,
  handleTableAction
}) {
  const [search, setSearch] = useState("");

  const matches = useMemo(() => freightMatches, [freightMatches]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "newlyAdded",
        width: 20,
        Cell: NewRowCell,
      },
      {
        Header: "Declined",
        id: "declined",
        width: 65,
        Cell: DeclinedMatchCell
      },
      {
        Header: "Actions",
        id: "actions",
        carrierId: activeCarrier.id,
        originPoint: "carrier-freight-matching",
        actions: ["carrier-offer", "shot-clock", "load-booking"],
        width: 128,
        Cell: ActionsCell,
      },
      {
        Header: "Entered By User",
        accessor: "enteredBy",
        filter: "fuzzyText",
        width: 145,
        Cell: DefaultCell,
      },
      {
        Header: "Load",
        accessor: "orderId",
        filter: "fuzzyText",
        width: 90,
        idKey: "orderId",
        Cell: LoadIdCell,
      },
      {
        Header: "Truck Origin",
        accessor: "carrierOriginCity",
        filter: "fuzzyText",
        width: 140,
        Cell: UpCaseCell,
      },
      {
        Header: "Truck OS",
        accessor: "carrierOriginState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 110,
        Cell: DefaultCell,
      },
      {
        Header: "Truck Mode",
        accessor: "mode",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 120,
        Cell: DefaultCell
      },
      {
        Header: "Origin Deadhead",
        accessor: "milesFromOrigin",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 155,
        Cell: MilesCell,
      },
      {
        Header: "Origin City",
        accessor: "originCity",
        filter: "fuzzyText",
        width: 140,
        Cell: UpCaseCell,
      },
      {
        Header: "OS",
        accessor: "originState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 75,
        Cell: DefaultCell,
      },
      {
        Header: "Origin Arrival",
        accessor: "originSchedArriveEarly",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        lateDate: "originSchedArriveLate",
        stopType: "Origin",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Dest City",
        accessor: "destinationCity",
        filter: "fuzzyText",
        width: 140,
        Cell: UpCaseCell,
      },
      {
        Header: "DS",
        accessor: "destinationState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 75,
        Cell: DefaultCell,
      },
      {
        Header: "Dest Arrival",
        accessor: "destinationSchedArriveEarly",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        lateDate: "destinationSchedArriveLate",
        stopType: "Destination",
        width: 145,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Dest Deadhead",
        accessor: "milesFromDestination",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 150,
        Cell: MilesCell,
      },
      {
        Header: "Customer",
        accessor: "customerName",
        filter: "fuzzyText",
        width: 320,
        Cell: DefaultCell,
      },
      {
        Header: "Eq. Type",
        accessor: "equipmentTypeId",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 105,
        Cell: DefaultCell,
      },
      {
        Header: "Commodity",
        accessor: "commodity",
        filter: "fuzzyText",
        width: 110,
        Cell: TitleCaseCell,
      },
      {
        Header: "Weight",
        accessor: "orderWeight",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 90,
        Cell: WeightCell,
      },
      {
        Header: "Max Pay",
        accessor: "maxPay",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 110,
        Cell: CurrencyCell,
      },
      {
        Header: "Source",
        accessor: "matchType",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 90,
        Cell: DefaultCell,
      },
    ],
    [activeCarrier]
  );

  const updateData = (rowIndex, columnID, value) => {
    setFreightMatches(updateFreightMatches(rowIndex, columnID, value, freightMatches));
  };

  return (
    <>
      <Box className="Matches-board-component-header">
        {activeCarrier.name &&
          <Typography variant="h3" color="black_opacity.100">
            {`Freight Matching for ${activeCarrier.name} (${freightMatches.length})`}
          </Typography>
        }
        <Box sx={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
          <StyledTooltip title="Refresh Matches">
            <Box sx={{ display: "flex", alignItems: "center", marginRight: "10px" }}>
              <InlineIconButton
                onClick={() => handleGetMatches()}
                size="20px"
                icon={CachedIcon}
                buttonColor={newLoadsCount > 0 ? "primary.60" : "black_solid.60"}
              />
              {newLoadsCount > 0 && <Typography variant="body1" color="primary.60">{newLoadsCount} NEW</Typography>}
            </Box>
          </StyledTooltip>
          <Box sx={{ display: "flex", marginRight: "10px" }}>
            <Typography variant="body1" color="black_opacity.100" sx={{ marginRight: "8px" }}>
              Origin:
            </Typography>
            <PrimarySelect
              options={radiusOptions}
              onChange={setRadiusOrigin}
              value={radiusOrigin}
              variant="standard"
              nameKey="name"
              valueKey="value"
              width="70px"
              disabled={isLoading}
            />
          </Box>
          <Box sx={{ display: "flex", marginRight: "10px" }}>
            <Typography variant="body1" color="black_opacity.100" sx={{ marginRight: "8px" }}>
              Dest:
            </Typography>
            <PrimarySelect
              options={radiusOptions}
              onChange={setRadiusDest}
              value={radiusDest}
              variant="standard"
              nameKey="name"
              valueKey="value"
              width="70px"
              disabled={isLoading}
            />
          </Box>
          <PrimaryTextField
            placeholder={`Search ${matches.length} results`}
            onChange={setSearch}
            value={search}
            isSearch
          />
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <CustomizedTable
          id="carrier-freight-matches"
          columns={columns}
          data={matches}
          tableSearch={search}
          updateData={updateData}
          actionCallback={handleTableAction}
        />
      )}
    </>
  );
}

CarrierFreightMatches.propTypes = {
  isLoading: PropTypes.bool,
  activeCarrier: PropTypes.object.isRequired,
  freightMatches: PropTypes.array.isRequired,
  setFreightMatches: PropTypes.func.isRequired,
  newLoadsCount: PropTypes.number,
  radiusOptions: PropTypes.array.isRequired,
  radiusOrigin: PropTypes.string,
  setRadiusOrigin: PropTypes.func.isRequired,
  radiusDest: PropTypes.string,
  setRadiusDest: PropTypes.func.isRequired,
  handleGetMatches: PropTypes.func.isRequired,
  handleTableAction: PropTypes.func,
};